/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';
import { useNavigate } from 'react-router-dom';
import PatientRowName from 'components/Misc/PatientRowName';
import { EMRS } from '../constants/EmrNoteTypeMapping';
// reactstrap components
import {
  Button,
  Card,
  CardBody,
  Input,
  Table,
  Container,
  Row,
  Col,
} from 'reactstrap';

import Header from 'components/Headers/Header.js';
import ConversationRowName from 'components/Misc/ConversationRowName';
import IntegrationRequestModal from 'components/Misc/IntegrationRequestModal';

// Device cards from the COM-471 design.
const APPS = [
  { name: 'Chrome extension', desc: 'Fills your notes right in your EMR.', cta: 'Install', href: 'https://chromewebstore.google.com/detail/comprehend-emr-integratio/pjafhckheppfdbidlhoedddfgebmcmnc', icon: 'M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9M2.7 18.55L4 16h16l1.3 2.55a1 1 0 0 1-.9 1.45H3.6a1 1 0 0 1-.9-1.45z' },
  { name: 'iPhone app', desc: 'Record visits from your iPhone.', cta: 'Download on App Store', href: 'https://apps.apple.com/us/app/comprehend-pt/id6499058918', icon: 'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM11 18h2' },
  { name: 'Android app', desc: 'Record visits from your Android phone.', cta: 'Get it on Google Play', href: 'https://play.google.com/store/apps/details?id=com.comprehendpt.app', icon: 'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM6 5h12M6 18h12' },
  // Watch app ships inside the iPhone app's App Store listing
  { name: 'Apple Watch', desc: 'Record visits from your wrist.', cta: 'Get it on your Watch', href: 'https://apps.apple.com/us/app/comprehend-pt/id6499058918', icon: 'M8 6h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM9 6l1-4h4l1 4M9 18l1 4h4l1-4M18 11h1' },
];

const Integrations = (props) => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestSent, setRequestSent] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    // If our session key is null we should redirect to login
    // Otherwise load the homepage
    if (!apiClient.haveSessionKey()) {
      console.log('Went home and got null session key. Forwarding to Login');
      navigate('/auth/login');
    }
  }, [apiClient, navigate]);

  const emrIcons = EMRS.filter(
    (emr) => emr.name !== 'WebPT2' && emr.name !== 'Comprehend Health',
  ).map((emr) => ({
    name: emr.name,
    src: emr.image,
  }));
  const shownEmrs = emrIcons.filter((emr) =>
    emr.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col xl="12">
            <Card className="shadow">
              <CardBody style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                <div>
                  <h1 className="mb-0" style={{ fontSize: 28, color: '#262C4D' }}>Integrations &amp; Apps</h1>
                  <p className="mb-0 mt-1" style={{ fontSize: 15, color: '#5E6680' }}>
                    Use Comprehend wherever you already document.
                  </p>
                </div>
                <section aria-labelledby="apps-h" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <h2 id="apps-h" className="mb-0" style={{ fontSize: 20, color: '#262C4D' }}>Devices</h2>
                    <p className="mb-0 mt-1" style={{ fontSize: 14, color: '#5E6680' }}>
                      Record visits on any device. Use the Chrome extension to fill your notes in your EMR.
                    </p>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))', gap: 14 }}>
                    {APPS.map((a) => (
                      <div key={a.name} style={{ border: '1px solid #E4E7EE', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div className="d-flex align-items-center" style={{ gap: 14 }}>
                          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#EAF7EF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F8A47" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d={a.icon} />
                            </svg>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <h3 className="mb-0" style={{ fontSize: 16, color: '#262C4D' }}>{a.name}</h3>
                            <p className="mb-0" style={{ fontSize: 14, color: '#5E6680', marginTop: 2 }}>{a.desc}</p>
                          </div>
                        </div>
                        <Button color="primary" href={a.href} target="_blank" rel="noopener noreferrer" style={{ alignSelf: 'flex-start' }}>
                          {a.cta}
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>
                <section aria-labelledby="emr-h" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="d-flex flex-wrap align-items-end justify-content-between" style={{ gap: 12 }}>
                    <div>
                      <h2 id="emr-h" className="mb-0" style={{ fontSize: 20, color: '#262C4D' }}>EMR integrations</h2>
                      <p className="mb-0 mt-1" style={{ fontSize: 14, color: '#5E6680' }}>
                        {query.trim()
                          ? `${shownEmrs.length} of ${emrIcons.length} EMRs`
                          : `Comprehend works with ${emrIcons.length} EMRs.`}
                      </p>
                    </div>
                    <div style={{ position: 'relative', flex: '0 1 300px' }}>
                      <label htmlFor="emr-q" className="sr-only">Search EMRs</label>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5E6680" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ position: 'absolute', left: 12, top: 13 }}>
                        <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4" />
                      </svg>
                      <Input id="emr-q" placeholder="Search EMRs" value={query} onChange={(e) => setQuery(e.target.value)} style={{ paddingLeft: 38, minHeight: 44 }} />
                    </div>
                  </div>
                  {shownEmrs.length > 0 ? (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
                      {shownEmrs.map((emr) => (
                        <li key={emr.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, border: '1px solid #E4E7EE', borderRadius: 12, padding: '18px 14px 14px', minHeight: 120, background: '#fff' }}>
                          <div style={{ height: 48, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={emr.src} alt="" style={{ maxHeight: 44, maxWidth: 140, objectFit: 'contain' }} />
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#3B4260' }}>{emr.name}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0" style={{ fontSize: 15, color: '#5E6680' }}>No EMRs match "{query.trim()}" yet.</p>
                  )}
                  <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 12, border: '1px dashed #B9C1D0', borderRadius: 12, padding: '18px 20px' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#262C4D' }}>Don't see your EMR?</div>
                      <div style={{ fontSize: 14, color: '#5E6680', marginTop: 2 }}>
                        {requestSent
                          ? `Request sent for ${requestSent}. We'll keep you posted.`
                          : 'We build new integrations quickly. Tell us which one you use.'}
                      </div>
                    </div>
                    <Button color="secondary" outline onClick={() => setRequestOpen(true)}>
                      Request an integration
                    </Button>
                  </div>
                </section>
                <IntegrationRequestModal
                  isOpen={requestOpen}
                  toggle={() => setRequestOpen(false)}
                  onSent={setRequestSent}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Integrations;
